import React from "react";
import { canManageSystemDiagnostics } from "../lib/accessControl.js";
import { createSystemDiagnosticsSnapshot } from "../lib/systemConfigApi.js";
import { readRuntimeRequestProfiles } from "../lib/runtimeRequestProfiling.js";
import { readDispatchDateFromSearch } from "../utils/dispatchDateUrls.js";
import {
  getDiagnosticsSnapshot,
  recordDiagnosticsSnapshotResult,
  setDiagnosticsNextSnapshotAt,
  subscribeToDiagnostics,
} from "./useDiagnostics.js";
import { getDataManagerDiagnosticsSnapshot } from "./dataManagerDiagnosticsState.js";
import { getPageDataDiagnosticsSnapshot } from "./usePageData.js";
import { getShellDataDiagnosticsSnapshot } from "./useShellData.js";

export const DIAGNOSTICS_AUTO_SNAPSHOT_INTERVAL_MS = 30 * 60 * 1000;
export const DIAGNOSTICS_AUTO_SNAPSHOT_LEADER_STORAGE_KEY = "grain:diagnostics:performance:leader";

const DEFAULT_CAPTURE_ERROR_MESSAGE = "Impossible d'enregistrer le snapshot diagnostic.";
const HEARTBEAT_INTERVAL_MS = 15 * 1000;
const LEADER_STALE_AFTER_MS = 45 * 1000;
const MIN_CAPTURE_DELAY_MS = 25;
const RECENT_RUNTIME_REQUEST_LIMIT = 10;

function resolveGlobalWindow(windowObject) {
  if (windowObject && typeof windowObject === "object") {
    return windowObject;
  }

  return typeof window === "object" && window ? window : null;
}

function resolveDiagnosticsSchedulerStorage(windowObject) {
  try {
    return resolveGlobalWindow(windowObject)?.localStorage ?? null;
  } catch {
    return null;
  }
}

function normalizeOptionalText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : "";
}

function normalizeOptionalInteger(value) {
  return Number.isFinite(value) && value >= 0 ? Math.round(value) : null;
}

function parseIsoTimestamp(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function resolveNowIso(nowMs) {
  return new Date(nowMs).toISOString();
}

function resolveFutureIso(nowMs, deltaMs) {
  return new Date(nowMs + Math.max(deltaMs, 0)).toISOString();
}

function createDiagnosticsTabId() {
  if (typeof globalThis?.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return `diag-tab-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function readLeaderRecord(storage) {
  if (!storage) {
    return null;
  }

  try {
    const rawValue = storage.getItem(DIAGNOSTICS_AUTO_SNAPSHOT_LEADER_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    return parsedValue && typeof parsedValue === "object" ? parsedValue : null;
  } catch {
    return null;
  }
}

function writeLeaderRecord(storage, record) {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(DIAGNOSTICS_AUTO_SNAPSHOT_LEADER_STORAGE_KEY, JSON.stringify(record));
  } catch {}
}

export function clearDiagnosticsAutoSnapshotLeaderForTests(storage = resolveDiagnosticsSchedulerStorage()) {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(DIAGNOSTICS_AUTO_SNAPSHOT_LEADER_STORAGE_KEY);
  } catch {}
}

export function isDiagnosticsAutoSnapshotLeaderStale(record, nowMs = Date.now()) {
  const heartbeatAt = parseIsoTimestamp(record?.heartbeatAt);
  if (heartbeatAt == null) {
    return true;
  }

  return nowMs - heartbeatAt > LEADER_STALE_AFTER_MS;
}

export function resolveNextDiagnosticsSnapshotDelay(snapshot, nowMs = Date.now(), intervalMs = DIAGNOSTICS_AUTO_SNAPSHOT_INTERVAL_MS) {
  const explicitNextAt = parseIsoTimestamp(snapshot?.nextSnapshotAt);
  if (explicitNextAt != null) {
    return Math.max(explicitNextAt - nowMs, 0);
  }

  const anchorTimestamp = parseIsoTimestamp(snapshot?.lastSnapshotAt) ?? parseIsoTimestamp(snapshot?.activatedAt);
  if (anchorTimestamp == null) {
    return 0;
  }

  return Math.max(anchorTimestamp + intervalMs - nowMs, 0);
}

function claimLeader({ nowMs, sessionId, storage, tabId }) {
  if (!storage) {
    return true;
  }

  const existingRecord = readLeaderRecord(storage);
  if (
    existingRecord
    && existingRecord.sessionId === sessionId
    && existingRecord.ownerTabId === tabId
  ) {
    writeLeaderRecord(storage, {
      heartbeatAt: resolveNowIso(nowMs),
      ownerTabId: tabId,
      sessionId,
    });
    return true;
  }

  if (
    existingRecord
    && existingRecord.sessionId === sessionId
    && !isDiagnosticsAutoSnapshotLeaderStale(existingRecord, nowMs)
  ) {
    return false;
  }

  writeLeaderRecord(storage, {
    heartbeatAt: resolveNowIso(nowMs),
    ownerTabId: tabId,
    sessionId,
  });

  const confirmedRecord = readLeaderRecord(storage);
  return Boolean(
    confirmedRecord
    && confirmedRecord.ownerTabId === tabId
    && confirmedRecord.sessionId === sessionId,
  );
}

function releaseLeader({ sessionId, storage, tabId }) {
  if (!storage) {
    return;
  }

  const currentRecord = readLeaderRecord(storage);
  if (!currentRecord) {
    return;
  }

  if (currentRecord.ownerTabId !== tabId || currentRecord.sessionId !== sessionId) {
    return;
  }

  clearDiagnosticsAutoSnapshotLeaderForTests(storage);
}

function buildPerformanceMemorySnapshot(windowObject) {
  const memory = windowObject?.performance?.memory;
  if (!memory || typeof memory !== "object") {
    return null;
  }

  return {
    jsHeapSizeLimit: normalizeOptionalInteger(memory.jsHeapSizeLimit),
    totalJSHeapSize: normalizeOptionalInteger(memory.totalJSHeapSize),
    usedJSHeapSize: normalizeOptionalInteger(memory.usedJSHeapSize),
  };
}

function buildRecentRuntimeRequestProfiles(profiles) {
  return profiles.slice(-RECENT_RUNTIME_REQUEST_LIMIT).map((entry) => ({
    durationMs: entry?.durationMs ?? null,
    method: normalizeOptionalText(entry?.method),
    path: normalizeOptionalText(entry?.path),
    recordedAt: normalizeOptionalText(entry?.recordedAt),
    requestBodyBytes: entry?.requestBodyBytes ?? null,
    responseBodyBytes: entry?.responseBodyBytes ?? null,
    runTotalMs: entry?.runtimeProfile?.run_total_ms ?? null,
    sqlTotalMs: entry?.runtimeProfile?.sql_total_ms ?? null,
    status: Number.isFinite(entry?.status) ? Number(entry.status) : null,
  }));
}

export function buildDiagnosticsAutoSnapshotPayload({
  diagnosticsSnapshot = getDiagnosticsSnapshot(),
  pageDataSnapshot = getPageDataDiagnosticsSnapshot(),
  shellDataSnapshot = getShellDataDiagnosticsSnapshot(),
  dataManagerSnapshot = getDataManagerDiagnosticsSnapshot(),
  runtimeProfiles = readRuntimeRequestProfiles(),
  nowMs = Date.now(),
  tabId,
  windowObject = resolveGlobalWindow(),
} = {}) {
  const location = windowObject?.location;
  const recentRuntimeProfiles = buildRecentRuntimeRequestProfiles(Array.isArray(runtimeProfiles) ? runtimeProfiles : []);
  const performanceMemory = buildPerformanceMemorySnapshot(windowObject);
  const capturedAt = resolveNowIso(nowMs);

  return {
    captured_at: capturedAt,
    diagnostics_run_started_at: diagnosticsSnapshot.activatedAt,
    diagnostics_session_id: diagnosticsSnapshot.diagnosticsSessionId || `diag-session-${capturedAt}`,
    frontend: {
      browser: {
        language: normalizeOptionalText(windowObject?.navigator?.language),
        userAgent: normalizeOptionalText(windowObject?.navigator?.userAgent),
      },
      data_manager: dataManagerSnapshot,
      diagnostics_store: {
        eventCount: diagnosticsSnapshot.eventCount,
        lastEventAt: diagnosticsSnapshot.lastEventAt,
        lastEventLabel: diagnosticsSnapshot.lastEventLabel,
        recentEvents: Array.isArray(diagnosticsSnapshot.recentEvents)
          ? diagnosticsSnapshot.recentEvents.slice(-10)
          : [],
        resetCount: diagnosticsSnapshot.resetCount,
        snapshotCount: diagnosticsSnapshot.snapshotCount,
      },
      page_cache: pageDataSnapshot,
      performance_memory: performanceMemory,
      runtime_requests: {
        recentEndpointProfiles: recentRuntimeProfiles,
        totalRecordedRequestCount: Array.isArray(runtimeProfiles) ? runtimeProfiles.length : 0,
      },
      shell_cache: shellDataSnapshot,
      tab_id: normalizeOptionalText(tabId),
    },
    route: {
      dispatch_date: readDispatchDateFromSearch(location?.search || "") || null,
      hash: normalizeOptionalText(location?.hash),
      pathname: normalizeOptionalText(location?.pathname) || "/",
      search: normalizeOptionalText(location?.search),
    },
    schema_version: "system_diagnostics_snapshot_v1",
  };
}

export function createDiagnosticsAutoSnapshotController({
  clearIntervalImpl = globalThis.clearInterval?.bind(globalThis),
  clearTimeoutImpl = globalThis.clearTimeout?.bind(globalThis),
  getDataManagerSnapshot = getDataManagerDiagnosticsSnapshot,
  getDiagnosticsState = getDiagnosticsSnapshot,
  getPageDataSnapshot = getPageDataDiagnosticsSnapshot,
  getShellDataSnapshot = getShellDataDiagnosticsSnapshot,
  intervalMs = DIAGNOSTICS_AUTO_SNAPSHOT_INTERVAL_MS,
  isAdmin = false,
  now = () => Date.now(),
  postSnapshot = createSystemDiagnosticsSnapshot,
  readRuntimeProfiles = readRuntimeRequestProfiles,
  recordSnapshotResult = recordDiagnosticsSnapshotResult,
  setIntervalImpl = globalThis.setInterval?.bind(globalThis),
  setNextSnapshotAt = setDiagnosticsNextSnapshotAt,
  setTimeoutImpl = globalThis.setTimeout?.bind(globalThis),
  storage = resolveDiagnosticsSchedulerStorage(),
  tabId = createDiagnosticsTabId(),
  windowObject = resolveGlobalWindow(),
} = {}) {
  let activeCapturePromise = null;
  let captureTimeoutId = null;
  let disposed = false;
  let heartbeatIntervalId = null;
  let isAdminEnabled = isAdmin === true;

  function clearCaptureTimeout() {
    if (captureTimeoutId != null && typeof clearTimeoutImpl === "function") {
      clearTimeoutImpl(captureTimeoutId);
    }
    captureTimeoutId = null;
  }

  function stopMonitoring() {
    if (heartbeatIntervalId != null && typeof clearIntervalImpl === "function") {
      clearIntervalImpl(heartbeatIntervalId);
    }
    heartbeatIntervalId = null;
  }

  function ensureMonitoring() {
    if (heartbeatIntervalId != null || typeof setIntervalImpl !== "function") {
      return;
    }

    heartbeatIntervalId = setIntervalImpl(() => {
      evaluate();
    }, HEARTBEAT_INTERVAL_MS);
  }

  async function captureSnapshot() {
    if (activeCapturePromise) {
      return activeCapturePromise;
    }

    const diagnosticsState = getDiagnosticsState();
    const startedAtMs = now();
    const nextSnapshotAt = resolveFutureIso(startedAtMs, intervalMs);

    if (!claimLeader({ nowMs: startedAtMs, sessionId: diagnosticsState.diagnosticsSessionId, storage, tabId })) {
      evaluate();
      return null;
    }

    const requestPayload = buildDiagnosticsAutoSnapshotPayload({
      dataManagerSnapshot: getDataManagerSnapshot(),
      diagnosticsSnapshot: diagnosticsState,
      nowMs: startedAtMs,
      pageDataSnapshot: getPageDataSnapshot(),
      runtimeProfiles: readRuntimeProfiles(),
      shellDataSnapshot: getShellDataSnapshot(),
      tabId,
      windowObject,
    });

    activeCapturePromise = Promise.resolve(postSnapshot(requestPayload))
      .then((response) => {
        recordSnapshotResult({
          capturedAt: response?.captured_at || requestPayload.captured_at,
          nextSnapshotAt,
          snapshotId: response?.snapshot_id || "",
          status: "success",
          storageRelativePath: response?.storage_relative_path || "",
        });
        return response;
      })
      .catch((error) => {
        recordSnapshotResult({
          errorMessage: error instanceof Error ? error.message : DEFAULT_CAPTURE_ERROR_MESSAGE,
          nextSnapshotAt,
          status: "error",
        });
        return null;
      })
      .finally(() => {
        activeCapturePromise = null;
        evaluate();
      });

    return activeCapturePromise;
  }

  function scheduleNextCapture() {
    clearCaptureTimeout();

    const diagnosticsState = getDiagnosticsState();
    if (!isAdminEnabled || diagnosticsState.isActive !== true) {
      return;
    }

    const nowMs = now();
    const plannedNextSnapshotAt = parseIsoTimestamp(diagnosticsState.nextSnapshotAt) != null
      ? diagnosticsState.nextSnapshotAt
      : resolveFutureIso(nowMs, resolveNextDiagnosticsSnapshotDelay(diagnosticsState, nowMs, intervalMs));
    const delayMs = Math.max((parseIsoTimestamp(plannedNextSnapshotAt) ?? nowMs) - nowMs, 0);

    if (diagnosticsState.nextSnapshotAt !== plannedNextSnapshotAt) {
      setNextSnapshotAt(plannedNextSnapshotAt);
    }

    if (typeof setTimeoutImpl !== "function") {
      return;
    }

    captureTimeoutId = setTimeoutImpl(() => {
      if (disposed) {
        return;
      }

      void captureSnapshot();
    }, Math.max(delayMs, MIN_CAPTURE_DELAY_MS));
  }

  function evaluate() {
    if (disposed) {
      return;
    }

    const diagnosticsState = getDiagnosticsState();
    if (!isAdminEnabled || diagnosticsState.isActive !== true) {
      clearCaptureTimeout();
      stopMonitoring();
      releaseLeader({ sessionId: diagnosticsState.diagnosticsSessionId, storage, tabId });
      return;
    }

    ensureMonitoring();
    const sessionId = diagnosticsState.diagnosticsSessionId;
    if (!sessionId || !claimLeader({ nowMs: now(), sessionId, storage, tabId })) {
      clearCaptureTimeout();
      return;
    }

    scheduleNextCapture();
  }

  return {
    dispose() {
      disposed = true;
      const diagnosticsState = getDiagnosticsState();
      clearCaptureTimeout();
      stopMonitoring();
      releaseLeader({ sessionId: diagnosticsState.diagnosticsSessionId, storage, tabId });
    },
    refresh({ isAdmin: nextIsAdmin } = {}) {
      if (typeof nextIsAdmin === "boolean") {
        isAdminEnabled = nextIsAdmin;
      }

      evaluate();
    },
    tabId,
  };
}

export default function useDiagnosticsAutoSnapshots({ access } = {}) {
  const diagnosticsSnapshot = React.useSyncExternalStore(
    subscribeToDiagnostics,
    getDiagnosticsSnapshot,
    getDiagnosticsSnapshot,
  );
  const controllerRef = React.useRef(null);
  const isAdmin = canManageSystemDiagnostics(access);

  React.useEffect(() => {
    controllerRef.current = createDiagnosticsAutoSnapshotController({ isAdmin });
    return () => {
      controllerRef.current?.dispose();
      controllerRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    controllerRef.current?.refresh({ isAdmin });
  }, [diagnosticsSnapshot.diagnosticsSessionId, diagnosticsSnapshot.isActive, diagnosticsSnapshot.nextSnapshotAt, isAdmin]);
}